import React, { useState, useEffect } from 'react';
import axios from 'axios';
import flatten from 'lodash/flatten';
// template components
import AssistiveText from '@nmx/template/dist/componentLibrary/components/AssistiveText';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Select from '@nmx/template/dist/componentLibrary/components/forms/Select';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// icons
import CaratRight from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/CaratRight';
// styles
import {
  FieldsetStyled,
  FormStyled,
  LegendStyled,
  LinkStyled,
  LoadingStyled,
  ULStyled,
} from './styles';

export default ({ config }) => {
  const pageSize = 25;
  const sortingAttribute = 'lastName';
  const [isLoading, setIsLoading] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [pcgs, setPcgs] = useState([]);
  const [optionsArray, setOptionsArray] = useState([{
    name: 'Please select a state...',
    value: '',
    disabled: true,
  }]);

  const formatData = (data) => {
    data.forEach((item) => {
      if (item !== null) {
        const newItem = {
          name: item.name,
          value: item.abbreviation,
          disabled: false,
        };
        setOptionsArray(prevOptionsArray => [...prevOptionsArray, newItem]);
      }
    });
  };

  useEffect(() => {
    axios.get(`${config.public.searchApiBaseUrl}/pcg/states`)
      .then(rsp => {
        formatData(rsp.data);
      })
      .catch(() => {
        // do nothing
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EVENT HANDLERS ///////////////////////////////////////
  const handleChange = (e) => {
    setSelectedState(e.target.value);
  };

  const mergeAndSort = (sortingAttr, teams, ...frs) => {
    // flatten frs because they come over as arrays within arrays
    const flattenedFrs = flatten(frs);

    return [...teams, ...flattenedFrs].sort((a, b) => a[sortingAttr].localeCompare(b[sortingAttr]));
  };

  const getNumberOfBatchesRequired = (frPcgsCount) => {
    return Math.ceil(frPcgsCount / pageSize);
  };

  const generateFrPromise = (pageNumber) => {
    return axios.get(`${config.public.searchApiBaseUrl}/fr-search-v2?isPcg=true&stateAbbreviation=${selectedState}&page[size]=${pageSize}&page[number]=${pageNumber}`);
  };

  const getAdditionalFrPcgs = (totalFrPcgs) => {
    // subtract 2 because we already snagged the first page's results and we rounded up
    const numberOfBatchesNeeded = getNumberOfBatchesRequired(totalFrPcgs) - 2;
    const promises = [];
    let x;
    for (x = 0; x <= numberOfBatchesNeeded; x += 1) {
      const pageNumber = x + 1;
      promises.push(generateFrPromise(pageNumber));
    }

    return Promise.all(promises);
  };

  useEffect(() => {
    setIsLoading(true);
    // initially grab the first batch of frs and all the teams
    const teamsPromise = axios.get(`${config.public.searchApiBaseUrl}/teams?isPcg=true&stateAbbreviation=${selectedState}`);
    const frsPromise = generateFrPromise(0);

    Promise.all([teamsPromise, frsPromise]).then((results) => {
      let combinedPcgs = [];
      const [teams, frs1] = results;

      // add a lastName attribute to teams so we can sort them once they're merged with Frs. There may be a better way to do this?
      const sortableTeams = teams.data.map(team => {
        return {
          ...team,
          lastName: team.name,
        };
      });

      const totalFrPcgs = frs1.headers['nmx-result-count'];
      // if the results show we have more than 25 frs returned, we need to grab more frs
      if (totalFrPcgs > pageSize) {
        // grab more FRs
        getAdditionalFrPcgs(totalFrPcgs).then(additionalPcgs => {
          const datas = additionalPcgs.map(r => {
            return r.data;
          });

          combinedPcgs = mergeAndSort(sortingAttribute, sortableTeams, [...frs1.data, ...flatten(datas)]);
          setPcgs(combinedPcgs);
          setIsLoading(false);
        });
      } else {
        combinedPcgs = mergeAndSort(sortingAttribute, sortableTeams, frs1.data);
        // there is less than or equal to 25 frs, display them
        setPcgs(combinedPcgs);
        setIsLoading(false);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState]);

  return (
    <SectionContainer
      id='private-wealth-management-pcg-directory'
      backgroundVariant='darkDefault'
      themeType='darkTheme'
      branding='pcg'>
      <Row align='center'>
        <Col
          xsmall={10}
          medium={8}>
          <Typography
            branding='pcg'
            themeType='darkTheme'
            component='h2'
            align='center'>
            Find the private client group advisor or firm <WidowBlock>nearest you.</WidowBlock>
          </Typography>
        </Col>
      </Row>
      <Row align='center'>
        <Col
          nested
          xsmall={10}
          medium={8}>
          <FormStyled
            method='POST'
            id='private-wealth-management-pcg-directory-form'
            action='/'>
            <Row>
              <Col>
                <FieldsetStyled>
                  <LegendStyled>
                    Select a state near you to find a private client group advisor
                  </LegendStyled>
                  <Select
                    id='private-wealth-management-pcg-directory-select'
                    themeType='darkTheme'
                    labelText='Select a state'
                    name='state'
                    value={selectedState}
                    onChange={handleChange}
                    onBlur={handleChange}
                    required={false}
                    inputType='select'
                    options={optionsArray}
                  />
                </FieldsetStyled>
              </Col>
            </Row>
          </FormStyled>
          <AssistiveText component='h3'>
            Private client group selection results
          </AssistiveText>
          {isLoading && (
            <Row align='center'>
              <Col>
                <LoadingStyled />
              </Col>
            </Row>
          )}
          {(selectedState !== '' && !isLoading) && (
            <Row>
              {pcgs.length ? (
                <Col>
                  <ULStyled>
                    {pcgs.map((pcg, index) => {
                      return (
                        <li key={`list-item-${index + 1}`}>
                          <LinkStyled
                            variant='primaryDeemphasize'
                            linkUrl={pcg.url}
                            endAdornment={CaratRight}
                            endAdornmentStylesReset
                            branding='pcg'
                            themeType='darkTheme'
                            isInternalPdf>
                            {pcg.name ? pcg.name : `${pcg.lastName}, ${pcg.firstName}`}
                          </LinkStyled>
                        </li>
                      );
                    })}
                  </ULStyled>
                </Col>
              ) : (
                <Row>
                  <Col medium={8}>
                    <Typography
                      themeType='darkTheme'
                      disableBottomPadding>
                      It looks like there aren&#39;t any Private Client Group firms in your state yet. Select a state near you to see a list of firms there, and keep in mind that all of our advisors are set up to work with you no matter where you live.
                    </Typography>
                  </Col>
                </Row>
              )}
            </Row>
          )}
        </Col>
      </Row>
    </SectionContainer>
  );
};

